import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import ApplicationRoles from "constants/ApplicationRoles";
import type Commande from "models/Commande";
import type PatchData from "models/PatchData";
import { Authorization } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import { useNavigate, useParams } from "react-router";
import graphService from "services/graphService";
import commandesService from "services/commandesService";
import type Article from "models/Article";
import articlesService from "services/articlesService";
import NotFoundErrorPage from "pages/errors/NotFoundErrorPage";
import type CommandeArticleHistory from "models/CommandeArticleHistory";
import ModalPageTemplate from "components/templates/ModalPageTemplate";
import ValidatorCommandeTemplate from "components/templates/ValidatorCommandeTemplate";
import { useDispatch } from "react-redux";
import type store from "store/store";
import CommandePageTemplateSkeleton from "components/templates/CommandePageTemplateSkeleton";
import { getAllCommandes, setLoadingCommandsIds } from "reducers/commandesReducer";
import type CommandeArticleFormModel from "models/CommandeArticleFormModel";
import { routesConfig } from "config/app-config";

/**
 * Page réservée aux Validateurs, listant les commandes en attente de validation
 */
function ValidatorCommandePage(): ReactNode {
  const { commandeId } = useParams();
  const [commande, setCommande] = useState<Commande>();
  // TODO utiliser useErrorHandler()
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [commentaire, setCommentaire] = useState<string>("");
  const [historyCommandes, setHistoryCommandes] = useState<Array<CommandeArticleHistory>>([]);
  const [articlesList, setArticlesList] = useState<Array<CommandeArticleFormModel>>([]);
  const [articles, setArticles] = useState<Array<Article>>([]);
  const [demandeur, setDemandeur] = useState<User | undefined>();

  const navigate = useNavigate();
  const dispatch = useDispatch<typeof store.dispatch>();

  useEffect(() => {
    async function getAgent(agentId: string): Promise<void> {
      const agents = await graphService.getAgentsByIds([agentId]);
      setDemandeur(agents.find((u) => u.id === agentId));
    }

    async function getCommande(): Promise<void> {
      if (commandeId == null) {
        // TODO meilleure gestion des erreurs
        return;
      }
      const fetchedCommande: Commande = await commandesService.getById(commandeId);
      fetchedCommande.articles.sort((a, b) => a.commandeArticleNumber - b.commandeArticleNumber);

      void getAgent(fetchedCommande.demandeurId);
      const allArticles = await articlesService.getAll();
      setArticles(allArticles);

      const commands = await commandesService.getCommandeHistory(
        fetchedCommande.demandeurId,
        fetchedCommande.commandeId
      );

      setCommande(fetchedCommande);

      setArticlesList([...fetchedCommande.articles]);

      const history: Array<CommandeArticleHistory> = [];
      commands.forEach((value) => {
        const a = allArticles.find((a1) => value.articleId === a1.id);
        if (a?.categorie != null) history.push({ categorie: a.categorie, ...value });
      });

      setHistoryCommandes(history);

      setIsLoading(false);
    }

    void getCommande();
    // TODO: revoir les dépendances lors d'une mise à jour des règles ESLint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function updateCommande(): Promise<void> {
    if (commandeId == null) {
      // TODO meilleure gestion des erreurs
      return;
    }
    setIsSending(true);
    dispatch(setLoadingCommandsIds(commandeId));

    const statutsPatches: Array<PatchData> = articlesList.map((value, index) => ({
      op: "replace",
      path: `/articles/${index}/statutArticle`,
      value: value.statutArticle,
    }));

    const quantitePatches: Array<PatchData> = [];
    articlesList.forEach((element, index) => {
      if (element.quantite !== commande?.articles[index].quantite) {
        quantitePatches.push({
          op: "replace",
          path: `/articles/${index}/quantite`,
          value: element.quantite.toString(),
        });
      }
    });

    const taillePatches: Array<PatchData> = [];
    articlesList.forEach((element, index) => {
      if (element.taille !== commande?.articles[index].taille) {
        taillePatches.push({
          op: "replace",
          path: `/articles/${index}/taille`,
          // Désactivation de la règle, cela garantira que même une valeur `number` soit envoyée sous forme de `string`
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-template-expression
          value: `${element.taille}`,
        });
      }
    });

    const allPatches: Array<PatchData> = statutsPatches
      .concat(quantitePatches)
      .concat(taillePatches);

    try {
      if (statutsPatches == null) {
        throw new Error("statutsPatches is null (ValidatorCommandePage updateCommande)");
      } else {
        if (commentaire.trim().length !== 0) {
          await commandesService.addComment(commandeId, commentaire);
        }
        await commandesService.patchCommande(commandeId, allPatches);
      }
    } catch (error) {
      // TODO error handling
      console.error(error);
    } finally {
      setIsSending(false);
      void dispatch(getAllCommandes());
    }
    navigate(routesConfig.validator.path);
  }

  if (isLoading) {
    return (
      <ModalPageTemplate returnPath={routesConfig.validator.path}>
        <CommandePageTemplateSkeleton />
      </ModalPageTemplate>
    );
  }
  if (commande == null || commandeId == null) {
    return (
      <ModalPageTemplate returnPath={routesConfig.validator.path}>
        <NotFoundErrorPage />
      </ModalPageTemplate>
    );
  }

  return (
    <ModalPageTemplate returnPath={routesConfig.validator.path}>
      <Authorization roles={ApplicationRoles.SSTMANAGER}>
        <ValidatorCommandeTemplate
          demandeur={demandeur}
          articlesList={articlesList}
          updateCommande={updateCommande}
          isSending={isSending}
          historyCommandes={historyCommandes}
          setArticlesList={setArticlesList}
          articles={articles}
          commentaire={commentaire}
          setCommentaire={setCommentaire}
          commande={commande}
        />
      </Authorization>
    </ModalPageTemplate>
  );
}

export default ValidatorCommandePage;
